import React, { useCallback, useEffect, useState } from 'react';
import { RiArrowRightSLine } from 'react-icons/ri';
import { useNavigate, useParams } from 'react-router-dom';
import axiosInstance from 'config/axiosInstance';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import user_icon from '../../../assets/icons/user.svg';
import TaskIconRenderer from '../Utility/TaskIconRenderer';
import DeleteClientDialog from '../../../reusable/Client/dialog/DeleteClientDialog';
import {
  secondsToHoursAndMinutes,
  timeStringToSeconds,
} from '../Utility/timeHelpers';
import { useTranslation } from 'react-i18next';

interface ClientData {
  name: string;
  description: string;
  tasks: string[];
  assign_drivers: string[];
}

interface DeleteClientData {
  id: string;
  clientName: string;
  assign_drivers: string;
  tasks: string;
}

interface DriverData {
  name: string;
  profile_picture: string | null;
}

interface Task {
  id: number;
  name: string;
  icon: string;
}

const ViewClient: React.FC = () => {
  const { t } = useTranslation(['client']);
  const [data, setData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [taskDetails, setTaskDetails] = useState<Task[]>([]);
  const [driverNames, setDriverNames] = useState<DriverData[]>([]);
  const [totalDurationWithTasks, setTotalDurationWithTasks] = useState<{
    hours: number;
    minutes: number;
  } | null>(null);
  const [showDeleteClientDialog, setShowDeleteClientDialog] =
    useState<boolean>(false);
  const [deleteClient, setDeleteClient] = useState<DeleteClientData | null>(
    null
  );
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const handleEditClick = () => {
    navigate(`/client/${id}/edit`);
  };

  const handleDeleteClick = () => {
    if (data && id !== undefined) {
      setDeleteClient({
        id: id,
        clientName: data.name,
        assign_drivers: data.assign_drivers.length + ' people',
        tasks: data.tasks.length.toString(),
      });
      setShowDeleteClientDialog(true);
    } else {
      console.error('Client ID is undefined.');
    }
  };

  const fetchTotalDuration = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `/v1/client_deliveries/summarize_spent_hour_with_tasks/?id__in=${id}&start_time=2024-01-01T00%3A00%3A00.000Z&stop_time=2024-12-31T00%3A00%3A00.000Z`
      );
      const hourSpentArray = response.data[0]?.summarize || [];
      const totalSeconds = hourSpentArray.reduce(
        (total: number, entry: any) => {
          return total + timeStringToSeconds(entry.hour_spent);
        },
        0
      );

      setTotalDurationWithTasks(
        totalSeconds > 0 ? secondsToHoursAndMinutes(totalSeconds) : null
      );
    } catch (error) {
      console.error('Failed to fetch total duration', error);
    }
  }, [id]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(`/v1/client_deliveries/${id}/`);
      setData(response.data);

      const taskResponses = await Promise.all(
        response.data.tasks.map((taskId: string) =>
          axiosInstance.get(`/v1/tasks/${taskId}/`)
        )
      );
      setTaskDetails(
        taskResponses.map((res) => ({
          id: res.data.id,
          name: res.data.name,
          icon: res.data.icon || 'default',
        }))
      );

      const driverResponses = await Promise.all(
        response.data.assign_drivers.map((driverId: string) =>
          axiosInstance.get(`/v1/drivers/${driverId}/`)
        )
      );
      setDriverNames(
        driverResponses.map((res) => ({
          name: res.data.name,
          profile_picture: res.data.profile_picture || null,
        }))
      );

      setError(null);
      fetchTotalDuration(); // Fetch total duration after fetching client data
    } catch (error) {
      console.error('Failed to fetch client data', error);
      setError('Failed to fetch client data');
    } finally {
      setLoading(false);
    }
  }, [id, fetchTotalDuration]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!data) {
    return <div>No client data available</div>;
  }

  return (
    <section className="flex flex-col p-4 space-y-4 h-screen overflow-y-auto">
      <div className="flex flex-row items-center gap-4 text-primary/300 font-medium text-subtitle-1">
        <h6>{t('manage_client.title')}</h6>
        <RiArrowRightSLine size={24} />
        <h6>{id}</h6>
        <RiArrowRightSLine size={24} />
        <h6 className="underline">{t('manage_client.detail')}</h6>
      </div>
      {/* Body Section */}
      <div className="flex flex-col rounded-md bg-white flex-grow">
        <div className="flex flex-col px-6 py-4 gap-4">
          <div className="flex justify-between">
            <h6 className="font-medium text-subtitle-1">
              {t('manage_client.client_info')}
            </h6>
            <div className="flex flex-row items-end gap-2 text-subtitle-2 text-neutral/100">
              <button
                className="flex flex-row items-center gap-2 px-2 py-3 rounded"
                onClick={handleEditClick}
              >
                <IconEdit stroke={1.5} size={20} />
                <p>{t('action.edit')}</p>
              </button>
              <button
                className="flex flex-row items-center gap-2 px-2 py-3 rounded"
                onClick={handleDeleteClick}
              >
                <IconTrash stroke={1.5} size={20} />
                <p>{t('action.delete')}</p>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-5">
            <div className="flex flex-col col-span-2 gap-6">
              <div className="flex flex-col gap-1">
                <label className="text-caption text-neutral/100">
                  {t('detail.client_name')} ({data.name.length}/30)
                </label>
                <p className=" h-10 border border-neutral/50 rounded py-2 px-4 text-neutral/300 text-caption">
                  {data.name}
                </p>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-caption text-neutral/100">
                  {t('detail.client_description')} ({data.description?.length}
                  /100)
                </label>

                <p className="h-20 border border-neutral/50 text-neutral/300 text-caption rounded py-2 px-4">
                  {data.description || '\u00A0'}
                </p>
              </div>
            </div>
            <div className="flex flex-row col-span-3 gap-10 pl-24">
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="totalDuration"
                  className="text-caption text-neutral/100"
                >
                  {t('detail.total_duration')}
                </label>
                <p className="text-medium text-h5 text-neutral/300">
                  {totalDurationWithTasks
                    ? `${totalDurationWithTasks.hours} hrs ${totalDurationWithTasks.minutes} min`
                    : totalDurationWithTasks !== undefined
                    ? '0 min'
                    : '-'}
                </p>
              </div>
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="activeTasks"
                  className="text-caption text-neutral/100"
                >
                  {t('detail.active_task')}
                </label>
                <p className="text-medium text-h5 text-neutral/300">
                  {data.tasks.length}
                </p>
              </div>
              <div className="flex flex-col w-full gap-1">
                <label
                  htmlFor="driverAssigned"
                  className="text-caption text-neutral/100"
                >
                  {t('detail.driver_assigned')}
                </label>
                <p className="text-medium text-h5 text-neutral/300">
                  {data.assign_drivers.length}
                </p>
              </div>
            </div>
          </div>
        </div>
        <hr />
        <div className="flex flex-col px-6 py-4 gap-4">
          <h4 className="font-medium text-subtitle-1"> {t('detail.tasks')}</h4>
          <div className="px-10 grid grid-cols-4 gap-4">
            {taskDetails.map((task) => (
              <div
                key={task.id}
                className="flex flex-row justify-between items-center border rounded-md h-16 px-2 gap-2"
              >
                <span className="w-full truncate">{task.name}</span>
                <TaskIconRenderer iconName={task.icon} />
              </div>
            ))}
          </div>
        </div>
        <hr />
        <div className="flex flex-col px-6 py-4 gap-4">
          <h4 className="font-medium text-subtitle-1">
            {t('detail.driver_assignments')} - {driverNames.length}{' '}
            {t('detail.drivers')}
          </h4>
          <div className="grid grid-cols-5 gap-3">
            {driverNames.map((driver, index) => (
              <div
                key={index}
                className="flex flex-row justify-between items-center border rounded-md h-16 px-3 gap-2"
              >
                <img
                  src={driver.profile_picture || user_icon}
                  alt="User"
                  className="h-10 w-10 object-cover rounded-full"
                />
                <span className="w-full truncate text-body-2">
                  {' '}
                  {driver.name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {showDeleteClientDialog && deleteClient && (
        <DeleteClientDialog
          id={deleteClient.id}
          name={deleteClient.clientName}
          isOpen={showDeleteClientDialog}
          onClose={() => setShowDeleteClientDialog(false)}
          fields={[
            { label: t('table.client_id'), value: deleteClient.id },
            { label: t('table.client_name'), value: deleteClient.clientName },
            {
              label: t('table.assignees'),
              value: deleteClient.assign_drivers,
            },
            { label: t('table.task_counts'), value: deleteClient.tasks },
          ]}
        />
      )}
    </section>
  );
};

export default ViewClient;
